import type { ComponentType } from 'react'
import { useSelector } from 'react-redux'

import { getPrescriptionStates } from '~/store/reducers/constants'
import type { PrescriptionTimelineItem } from '~/types'

import { BaseRxCard } from '../BaseRxCard'
import PrescriptionsCardDetails from './PrescriptionsCardDetails'
import { convertToRxCardItem } from './rxCardItem'
import {
  ReviewApproveButton,
  useShowReviewApproveButton,
} from './rxTracker/ReviewApproveButton'
import {
  getPrescriptionStateName,
  getPrescriptionWithOrigin,
} from './rxTracker/rxCardUtilities'

interface RxPrescriptionsCardProps {
  actionNeeded?: boolean
  icon?: ComponentType<{ className?: string }>
  id?: string
  index: number
  isInvoice?: boolean
  item: PrescriptionTimelineItem
  onClick?: () => void
  titlePinColor?: string | null
}

/**
 * Card component for displaying prescriptions in the timeline.
 * Thin wrapper that handles prescription-specific logic (origin, approval button)
 * and delegates rendering to BaseRxCard.
 */
const RxPrescriptionsCard = ({
  item: itemProp,
  ...props
}: RxPrescriptionsCardProps) => {
  const prescriptionStates = useSelector(getPrescriptionStates)
  const item = convertToRxCardItem(getPrescriptionWithOrigin(itemProp))

  const prescriptionStateName = getPrescriptionStateName(
    item.prescription,
    prescriptionStates,
  )

  const shouldShowApproveButton = useShowReviewApproveButton(
    item.prescription,
    prescriptionStateName ?? null,
  )

  return (
    <BaseRxCard
      item={item}
      {...props}
      renderAdditionalContent={
        shouldShowApproveButton
          ? () => (
              <PrescriptionsCardDetails
                hideDivider
                approvalAction={
                  <ReviewApproveButton
                    prescription={item.prescription}
                    prescriptionStateName={prescriptionStateName ?? ''}
                  />
                }
              />
            )
          : undefined
      }
    />
  )
}

export default RxPrescriptionsCard
